import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatError } from "@/lib/api";
import { toast } from "sonner";
import { Receipt, CheckCircle2, Clock, XCircle } from "lucide-react";

function StatusPill({ status }) {
  if (status === "paid") return (
    <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2 py-1 text-xs font-extrabold uppercase text-green-700"><CheckCircle2 className="h-3 w-3" /> Paid</span>
  );
  if (status === "unpaid") return (
    <span className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2 py-1 text-xs font-extrabold uppercase text-red-700"><XCircle className="h-3 w-3" /> Unpaid</span>
  );
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-1 text-xs font-extrabold uppercase text-amber-800"><Clock className="h-3 w-3" /> Pending</span>
  );
}

export default function BillingHistory() {
  const [rows, setRows] = useState(null);

  useEffect(() => {
    api.get("/payments/mine").then((r) => setRows(r.data)).catch((e) => toast.error(formatError(e)));
  }, []);

  return (
    <div className="mx-auto max-w-4xl px-4 py-10 sm:px-6" data-testid="billing-page">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl font-extrabold text-sarthi-text sm:text-4xl">Billing history</h1>
          <p className="mt-1 text-base font-medium text-sarthi-muted">Every payment you've made on Sarthi Associates.</p>
        </div>
        <Link to="/plans" data-testid="plans-link" className="sarthi-btn-outline hidden px-4 py-2 sm:inline-block">See plans →</Link>
      </div>

      {rows === null && <div className="sarthi-card p-6 text-center text-sarthi-muted">Loading…</div>}

      {rows && rows.length === 0 && (
        <div className="sarthi-card p-10 text-center">
          <Receipt className="mx-auto h-14 w-14 text-sarthi-muted" strokeWidth={2} />
          <div className="mt-4 font-display text-xl font-extrabold text-sarthi-text">No payments yet</div>
          <div className="mt-1 text-sm font-medium text-sarthi-muted">Upgrade a job or grab blast credits to see receipts here.</div>
          <Link to="/plans" className="sarthi-btn-primary mt-5 inline-block px-6 py-3 text-white">Browse plans</Link>
        </div>
      )}

      {rows && rows.length > 0 && (
        <div className="sarthi-card overflow-hidden p-0">
          <table className="w-full text-sm">
            <thead className="bg-sarthi-accent text-left">
              <tr>
                <th className="px-4 py-3 font-bold uppercase tracking-wide text-sarthi-muted">Date</th>
                <th className="px-4 py-3 font-bold uppercase tracking-wide text-sarthi-muted">Package</th>
                <th className="px-4 py-3 font-bold uppercase tracking-wide text-sarthi-muted">Amount</th>
                <th className="px-4 py-3 font-bold uppercase tracking-wide text-sarthi-muted">Status</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((tx) => (
                <tr key={tx.session_id} data-testid={`billing-row-${tx.session_id}`} className="border-t border-sarthi-border">
                  <td className="px-4 py-3 text-sarthi-muted">{tx.created_at ? new Date(tx.created_at).toLocaleString() : ""}</td>
                  <td className="px-4 py-3">
                    <div className="font-bold text-sarthi-text">{tx.package_label}</div>
                    {tx.job_id && <Link to={`/jobs/${tx.job_id}`} className="text-xs font-semibold text-sarthi-primary underline">View job</Link>}
                  </td>
                  <td className="px-4 py-3 font-bold text-sarthi-text">₹{tx.amount} <span className="text-xs font-semibold text-sarthi-muted">{(tx.currency || "").toUpperCase()}</span></td>
                  <td className="px-4 py-3"><StatusPill status={tx.payment_status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
